'use client'

import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { useLayoutEffect, type ReactNode } from 'react'
import { isIgnoredBrowserError } from '@/lib/observability/ignored-browser-errors'
import {
  classifyPostHogEvent,
  POSTHOG_EVENT_CONTRACT_VERSION,
  redactPostHogProperties,
  requireCanonicalPostHogEventName,
} from '@/lib/analytics/posthog-contract'
import { getPostHogWorkspaceContext } from '@/lib/analytics/posthog-runtime-context'

type PostHogIdentityType = 'anonymous' | 'user' | 'workspace'

function classifyPostHogIdentity(
  properties: Record<string, unknown>,
  workspaceId: string | null
): PostHogIdentityType | null {
  const distinctId = properties.distinct_id
  if (typeof distinctId === 'string' && distinctId.startsWith('workspace:')) {
    return workspaceId && distinctId === `workspace:${workspaceId}` ? 'workspace' : null
  }

  const userId = properties.user_id ?? properties.$user_id
  if (
    properties.$is_identified === true ||
    (typeof userId === 'string' && userId.trim().length > 0)
  ) {
    return 'user'
  }

  return workspaceId ? 'workspace' : 'anonymous'
}

/**
 * PostHog client provider.
 *
 * All product features enabled per explicit user direction:
 *   - autocapture (click/change/submit)
 *   - session recording with all text and inputs masked
 *   - heatmaps + dead-click + rage-click detection
 *   - exception capture (browser-side errors)
 *   - surveys (in-app prompts)
 *   - feature flags + experiments
 *   - web vitals
 *   - console log capture disabled
 *   - cross-subdomain cookie persistence
 *
 * The proxy host `/ingest` routes through the Next.js rewrite in next.config
 * to bypass ad blockers and keep the call path on the same origin.
 *
 * Skipped in development so localhost noise doesn't pollute production data.
 */
export function PostHogProvider({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    if (process.env.NODE_ENV === 'development') return

    // Trim defensively: a stray newline in NEXT_PUBLIC_POSTHOG_KEY (easy to
    // introduce when pasting into Vercel) makes /flags/ POSTs return 401 and
    // the SDK records `$feature_flag_errors: ["api_error"]`, which suppresses
    // autocapture event firing.
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY?.trim()
    if (!key) return

    posthog.init(key, {
      defaults: '2026-01-30',
      api_host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST ?? '/ingest',
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ?? 'https://us.posthog.com',
      person_profiles: 'always',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      capture_performance: {
        web_vitals: true,
        // The SDK bounds INP/LCP attribution to scalar timing/target fields.
        // Keep CLS measurement, but avoid its attribution retaining detached DOM nodes.
        web_vitals_attribution: ['INP', 'LCP'],
      },
      autocapture: true,
      enable_recording_console_log: false,
      capture_exceptions: true,
      before_send: (event) => {
        if (!event || isIgnoredBrowserError(event)) return null
        if (!event.event.startsWith('$')) {
          try {
            requireCanonicalPostHogEventName(event.event)
          } catch {
            return null
          }
        }
        const properties = redactPostHogProperties(event.properties ?? {})
        const workspaceId = getPostHogWorkspaceContext()
        const identityType = classifyPostHogIdentity(properties, workspaceId)
        if (!identityType) return null
        return {
          ...event,
          properties: {
            ...properties,
            // PostHog requires its public project token inside every event
            // payload to route the event. The generic privacy redactor masks
            // token-shaped fields, so restore only the configured project
            // token here while leaving auth and arbitrary token fields masked.
            token: key,
            $groups: workspaceId ? { workspace: workspaceId } : {},
            workspace_id: workspaceId,
            event_contract_version: POSTHOG_EVENT_CONTRACT_VERSION,
            event_source: 'web_client',
            identity_type: identityType,
            event_classification: event.event.startsWith('$')
              ? 'system'
              : classifyPostHogEvent(event.event),
          },
        }
      },
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        recordCrossOriginIframes: false,
      },
      disable_session_recording: false,
      enable_heatmaps: true,
      cross_subdomain_cookie: true,
      persistence: 'localStorage+cookie',
      loaded: (ph) => {
        if (process.env.NODE_ENV !== 'production') {
          ph.debug()
        }
      },
    })
  }, [])

  return <PHProvider client={posthog}>{children}</PHProvider>
}
